import { apiEnvelope } from './api';

/**
 * A person in the CUSTOMER'S OWN calendar directory — their Google Workspace or
 * their Microsoft 365 tenant.
 *
 * Not a ZenSpace user. ZenCore knows whoever was invited into the ZenSpace
 * organization, usually a handful of administrators; this is everybody who can
 * actually put a meeting into one of these rooms.
 *
 * Nothing here is stored by the adapter — it is the provider's directory, read
 * live — which is why there is no local id and no cache.
 */
export interface DirectoryPerson {
  externalId: string;
  email: string | null;
  name: string | null;
  jobTitle: string | null;
  department: string | null;
  officeLocation: string | null;
  /** `archived` is Google-only: no licence, cannot sign in, history kept. */
  status: 'active' | 'suspended' | 'archived';
  /** Null where the provider will not say without a further permission. */
  isAdmin: boolean | null;
  lastSignInAt: string | null;
}

export interface DirectoryConnection {
  id: string;
  provider: string;
  provider_name: string;
  domain: string | null;
}

export interface DirectoryPeopleResult {
  /** Null when no calendar is connected yet. */
  connection: DirectoryConnection | null;
  /** Every live connection, so the screen can offer the choice of source. */
  connections: DirectoryConnection[];
  people: DirectoryPerson[];
  /** Opaque. Hand it back as `cursor` to read the next page. */
  next_cursor: string | null;
  /** The grant is the problem, not the person asking. */
  needs_reconsent: boolean;
  /** Carries the difference between "nobody" and "we were not allowed". */
  message: string;
}

export interface DirectoryQuery {
  search?: string;
  limit?: number;
  /** `active` hides suspended and archived accounts, at the provider. */
  status?: 'any' | 'active';
  /**
   * CURSOR, NOT PAGE NUMBER. Both providers hand back a cursor and neither will
   * skip to page four, so the screen keeps the ones it has seen rather than
   * pretending it can jump.
   */
  cursor?: string | null;
  connectionId?: string;
}

export const directoryApi = {
  people: async (query: DirectoryQuery = {}): Promise<DirectoryPeopleResult> => {
    const params = new URLSearchParams();
    params.set('limit', String(query.limit ?? 25));
    if (query.search) params.set('search', query.search);
    if (query.cursor) params.set('cursor', query.cursor);
    if (query.connectionId) params.set('connection_id', query.connectionId);
    if (query.status && query.status !== 'any') params.set('status', query.status);

    const envelope = await apiEnvelope<Omit<DirectoryPeopleResult, 'message'>>(
      `/calendar/directory/people?${params.toString()}`,
    );

    return {
      connection: envelope.data?.connection ?? null,
      connections: envelope.data?.connections ?? [],
      people: envelope.data?.people ?? [],
      next_cursor: envelope.data?.next_cursor ?? null,
      needs_reconsent: envelope.data?.needs_reconsent ?? false,
      message: envelope.message,
    };
  },
};
